import { useTandemTradingBlocked } from '../../../features/tandem/TandemSessionBoundary';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Button,
  Radio,
  InputNumber,
  Slider,
  Card,
  Row,
  Col,
  Input,
  message,
} from 'antd';

import {
  selectors,
  createOrder,
  SHORT_SELL,
  BUY,
  SELL,
  CASH_APPROACH,
  TERMS,
  ANALYSIS_TYPES,
  utils,
  REAL_TIME_SCENARIO,
  OrderType,
  HISTORICAL_SCENARIO,
} from 'state';
import { formatters } from 'services';
import {
  OverLimitIcon,
  CannotSellIcon,
  CannotShortSellIcon,
  CannotBuyIcon,
  CannotSellPopover,
  CannotShortPopover,
  CannotSellBecauseHTMisLocked,
  CannotShortSellIcon2,
  CannotCoverMargin,
  MarketNotOpenPopover,
} from './InfoPanels';
import useMaxQuantity from './useMaxQuantity';
import usePrices from './usePrices';
import useCollateral from './useCollateral';
import useCollateralSign from './useCollateral';
import * as util from 'util';
import { odaOfInstrumentCreateSelector } from '../../../state/app/selectors';
import { assetIsOpenNew, useReportingCurrency } from '../../../state/app/utils';
import { eq } from 'lodash';
import {
  notionalCapacityStatus,
  resolveNotionalMaximum,
  useNotionalCapacity,
} from '../useNotionalCapacity';

const { TextArea } = Input;

type Props = {
  visible: boolean;
  hide: () => void;
  id: number;
};

const OrderModal: React.FC<Props> = ({ visible, hide, id }) => {
  const tandemBlocked = useTandemTradingBlocked();
  const dispatch = useDispatch();

  const session = useSelector(selectors.activeSession);
  const {
    transaction_fee,
    limit_order_collateral,
    risk_approach,
    scenario_type,
    allow_short_sell,
    allow_short_only_on_fx,
    starting_cash_amount,
    short_sell_leverage,
    bond_leverage,
    equity_leverage,
    etf_equities_leverage,
    etf_commodities_leverage,
    futures_leverage,
  } = session;
  const assetList = useSelector(selectors.underlyingAssetsList);
  const currencyList = useSelector(selectors.currenciesList);

  const player = useSelector(selectors.player);
  const instruments = useSelector(selectors.financialInstruments);
  const instrument = useSelector(selectors.financialInstrumentById(id));
  const asset = useSelector(selectors.underlyingAssetOfInstrument(instrument));
  const isHtm = asset.resourcetype === 'HTMBond';
  const fxShortSell = allow_short_only_on_fx && asset.resourcetype === 'FX';
  const allowShortSell = (allow_short_sell && !isHtm) || fxShortSell;
  const currency = useSelector(selectors.currencyById(asset.base_currency));
  const reportingCurrency = useReportingCurrency(session.id);
  const assetMarketHours = useSelector(selectors.assetMarketHours(asset));
  const [isOpen] = utils.assetIsOpenNew(asset, session, assetMarketHours);

  const [quantity, setQuantity] = useState(100);
  const [orderType, setOrderType] = useState<OrderType>(
    isOpen ? 'MarketOrder' : 'LimitOrder',
  );
  useEffect(() => {
    if (!isOpen) {
      setOrderType('LimitOrder');
    }
  }, [assetMarketHours]);
  const [transaction, setTransaction] = useState(BUY);

  let { price, reportingPrice } = usePrices(id, transaction);
  // console.log(price, reportingPrice);
  const [investmentRationale, setInvestmentRationale] = useState<string>();
  const [slideValue, setSlideValue] = useState(0);
  const [forecastValue, setForecastValue] = useState(price || 0);
  const [editedForecast, setEditedForecast] = useState(false);
  const [term, setTerm] = useState();
  const [analysis, setAnalysis] = useState();

  useEffect(() => {
    //pass
  }, [instruments, dispatch]);

  // console.log(session.require_investment_rationale);
  // console.log(investmentRationale);
  let satisfiesInvestmentRationaleRequirement;
  let investmentRationalePlaceholder;
  if (session.require_investment_rationale === false) {
    satisfiesInvestmentRationaleRequirement = true;
    investmentRationalePlaceholder = 'Investment Rationale';
  } else {
    investmentRationalePlaceholder =
      'Investment Rationale | required field min. 10 char.';
    if (
      investmentRationale !== undefined &&
      investmentRationale !== '' &&
      investmentRationale.length > 9
    ) {
      satisfiesInvestmentRationaleRequirement = true;
    } else {
      satisfiesInvestmentRationaleRequirement = false;
    }
  }
  // console.log(satisfiesInvestmentRationaleRequirement);
  const isFX = asset.resourcetype === 'FX';
  const isBond =
    asset.resourcetype == 'GovtBond' ||
    asset.resourcetype == 'CorporateBond' ||
    asset.resourcetype == 'HTMBond' ||
    asset.resourcetype == 'AFSBond';
  const isEquity = asset.resourcetype == 'Equity';
  const isETFEquities = asset.resourcetype == 'ETFEquities';
  const isETFCommodities = asset.resourcetype == 'ETFCommodities';
  const isFutures = asset.resourcetype == 'Futures';
  const isBondAndSell = isBond && transaction === SELL;
  const isBondAndRealTime =
    isBond && session.scenario_type === REAL_TIME_SCENARIO;
  let initialLimitPrice = price;
  // console.log(`initialLimitPrice1: ${initialLimitPrice}`);
  // console.log(`asset.accrued_interest: ${asset.accrued_interest}`);
  // console.log(`price * 100: ${price * 100}`);
  if (isBond) {
    initialLimitPrice = price * 100 - asset.accrued_interest;
  }
  // console.log(`initialLimitPrice2: ${initialLimitPrice}`);
  const [limitPrice, setLimitPrice] = useState(initialLimitPrice || 0);
  const isLeveragedBond =
    isBond && bond_leverage != 1 && risk_approach === CASH_APPROACH;
  const isLeveragedEquity =
    isEquity && equity_leverage != 1 && risk_approach === CASH_APPROACH;
  const isLeveragedETFEquities =
    isETFEquities &&
    etf_equities_leverage != 1 &&
    risk_approach === CASH_APPROACH;
  const isLeveragedETFCommodities =
    isETFCommodities &&
    etf_commodities_leverage != 1 &&
    risk_approach === CASH_APPROACH;
  const isLeveragedFutures =
    isFutures && futures_leverage != 1 && risk_approach === CASH_APPROACH;

  const isLeveraged =
    isLeveragedEquity ||
    isLeveragedETFEquities ||
    isLeveragedETFCommodities ||
    isLeveragedBond ||
    isLeveragedFutures;
  let leverage = 1;
  if (isLeveragedBond) {
    leverage = bond_leverage;
  }
  if (isLeveragedEquity) {
    leverage = equity_leverage;
  }
  if (isLeveragedETFEquities) {
    leverage = etf_equities_leverage;
  }
  if (isLeveragedETFCommodities) {
    leverage = etf_commodities_leverage;
  }
  if (isLeveragedFutures) {
    leverage = futures_leverage;
  }
  const leveragedBondCollateralClause =
    isLeveraged && orderType === 'LimitOrder';
  const { maxQuantityHardLimits, maxQuantitySoftLimits } = useMaxQuantity(
    id,
    transaction,
    limitPrice,
    orderType,
  );
  console.log(`maxQuantityHardLimits: ${maxQuantityHardLimits}`);
  console.log(`maxQuantitySoftLimits: ${maxQuantitySoftLimits}`);
  let maxQuantity = session.hard_limits
    ? maxQuantityHardLimits
    : maxQuantitySoftLimits;
  if (isFX) {
    maxQuantity = maxQuantityHardLimits;
  }
  const notionalCapacity = useNotionalCapacity(id, transaction, visible);
  maxQuantity = resolveNotionalMaximum({
    localMaximum: maxQuantity,
    capacity: notionalCapacity,
    transaction,
    signedPosition: instrument.amount,
  });
  const capacityStatus = notionalCapacityStatus(notionalCapacity);
  const v2CapacityApplied =
    notionalCapacity.usesBackend && notionalCapacity.result?.applies !== false;
  console.log(`maxQuantity: ${maxQuantity}`);
  // console.log(maxQuantity);
  // 14168

  // Futures: the engine trades in units, but the player thinks in CONTRACTS (1 contract = lot_size units).
  // `quantity` stays in units (source of truth); the input below is a contracts view over it, and the
  // submit path is unchanged. lotSize defaults to 1 (=> contracts == units) so nothing changes for
  // fully-unset/legacy futures, and this block is futures-gated so other asset classes are untouched.
  const lotSize = isFutures ? asset.lot_size || 1 : 1;
  const maxContracts = Math.floor(maxQuantity / lotSize);
  const quantityTooLarge = quantity > maxQuantity;
  const quantityInvalid = quantity <= 0 || quantityTooLarge;
  const quantityInputStatus = quantityTooLarge ? 'error' : undefined;
  const quantityInputStyles = quantityTooLarge
    ? { input: { color: '#d54848' } }
    : undefined;

  // Start a futures order at 1 contract (default quantity is 100 units, which may not be a clean
  // multiple of lot_size). Keyed on asset.id so it only re-initializes when the modal targets a new asset.
  useEffect(() => {
    if (isFutures) {
      setQuantity(lotSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  const collateralSign = useCollateralSign(id, transaction);

  if (!instrument) {
    message.error('Could not retrieve financial instrument!');
    return null;
  }
  if (!price) {
    message.error('Could not retrieve price!');
    return null;
  }

  const wallet = player.wallets.find(
    wallet => asset.variable_currency === wallet.currency,
  );
  if (!wallet) {
    message.error('Could not retrieve wallet!');
    return null;
  }
  let displayBondPrice = formatters.commas2Digits(
    price * 100 - asset.accrued_interest,
  );
  let displayCleanPrice = `${formatters.commas2Digits(
    price * 100 - asset.accrued_interest,
  )} %`;
  let displayAccruedInterest = `${formatters.commas2Digits(
    asset.accrued_interest,
  )} %`;
  if (orderType === 'LimitOrder') {
    // value = quantity * limitPrice;
    price = limitPrice;
    if (isBond) price /= 100;
    reportingPrice = utils.cashExchange(
      asset.base_currency,
      session.reporting_currency,
      price,
      0,
      assetList,
      currencyList,
    );
    displayBondPrice = formatters.commas2Digits(price * 100);
    displayCleanPrice = '-';
    displayAccruedInterest = '-';
    // if (isBond) value /= 100;
  }
  const value = quantity * price;
  const total = value * (1 + transaction_fee);
  let collateral;
  if (collateralSign === -1) {
    collateral =
      (Math.abs(quantity) / Math.abs(instrument.amount)) *
      instrument.collateral *
      -1;
  } else {
    collateral = collateralSign * total * leverage;
  }
  // if (orderType === 'LimitOrder') collateral = 0;
  const cantCoverCollateral = isLeveraged && collateral > wallet.amount;
  const displayCollateral = cantCoverCollateral ? (
    <Row style={{ color: 'red' }}>
      Margin: {formatters.commasNoDigits(collateral)}
    </Row>
  ) : (
    <Row>Margin: {formatters.commasNoDigits(collateral)}</Row>
  );

  // INFO PANEL LOGIC
  const assetLimit = instrument.pv_limit * starting_cash_amount;
  const showOverLimitIcon =
    !v2CapacityApplied &&
    transaction === BUY &&
    reportingPrice * (instrument.amount + 1) >= assetLimit &&
    !isLeveraged;
  const showCannotSellIcon = transaction === SELL && instrument.amount === 0;
  const showCannotShortIcon2 =
    !v2CapacityApplied &&
    transaction === SHORT_SELL &&
    maxQuantity === 0 &&
    !isLeveraged;
  const showCannotShortIcon =
    player.remaining_short_sell_amount_limit < 1000 &&
    transaction === SHORT_SELL;
  const cannotBuy =
    transaction === BUY &&
    wallet.amount < price &&
    asset.resourcetype !== 'FX' &&
    !isLeveraged;

  const disableHTMSellButton = isHtm && !session.allow_htm;
  const disableSellButton = instrument.amount < 0;
  const disableShortButton = instrument.amount > 0;

  // const isFX = asset.resourcetype == 'FX';
  // const notFXnotBond = (isBond && isFX);

  const isNotBondOrFXnonGBP =
    asset.resourcetype !== 'GovtBond' &&
    asset.resourcetype !== 'CorporateBond' &&
    asset.resourcetype !== 'HTMBond' &&
    asset.resourcetype !== 'AFSBond' &&
    asset.resourcetype !== 'FX' &&
    currency.symbol !== 'GBP';

  const isNotBondOrFXGBP =
    asset.resourcetype !== 'GovtBond' &&
    asset.resourcetype !== 'CorporateBond' &&
    asset.resourcetype !== 'HTMBond' &&
    asset.resourcetype !== 'AFSBond' &&
    asset.resourcetype !== 'FX' &&
    currency.symbol === 'GBP';

  const { name } = asset;

  const showMooMocRadioButtons =
    scenario_type === REAL_TIME_SCENARIO && asset.resourcetype !== 'Crypto';
  // console.log('showMooMocRadioButtons: ', showMooMocRadioButtons);
  return (
    <Modal
      open={visible}
      title={`Order ${name}`}
      onOk={hide}
      onCancel={hide}
      footer={[
        <Button
          key="place"
          type="primary"
          disabled={
            tandemBlocked ||
            quantityInvalid ||
            cantCoverCollateral ||
            !satisfiesInvestmentRationaleRequirement
          }
          onClick={() => {
            if (quantityInvalid) return;
            dispatch(
              createOrder({
                resourcetype: orderType,
                financial_instrument: instrument.id,
                forecast: editedForecast ? forecastValue : 0,
                transaction,
                quantity,
                limit_price: limitPrice,
                investment_rationale: investmentRationale,
                term,
                analysis,
                hedge: false,
              }),
            );
            hide();
          }}
        >
          Place Order
        </Button>,
      ]}
    >
      Transaction:{' '}
      <Radio.Group
        onChange={e => setTransaction(e.target.value)}
        value={transaction}
      >
        <Radio value={BUY}>Buy</Radio>

        {isHtm ? (
          <CannotSellBecauseHTMisLocked visible={disableHTMSellButton}>
            <Radio value={SELL} disabled={disableHTMSellButton}>
              Sell
            </Radio>
          </CannotSellBecauseHTMisLocked>
        ) : (
          <CannotSellPopover visible={disableSellButton}>
            <Radio value={SELL} disabled={disableSellButton}>
              Sell
            </Radio>
          </CannotSellPopover>
        )}

        {allowShortSell && (
          // {isFX && (
          <CannotShortPopover visible={disableShortButton}>
            <Radio value={SHORT_SELL} disabled={disableShortButton}>
              Short Sell
            </Radio>
          </CannotShortPopover>
        )}
      </Radio.Group>
      <br />
      Price: ({currency.symbol}) {/*this gets you the clean price*/}
      {isBond && (
        <div style={{ display: 'inline-block' }}>{displayBondPrice} %</div>
      )}
      {isFX && formatters.commas4Digits(price)}
      {isNotBondOrFXnonGBP && formatters.commas2Digits(price)}
      {isNotBondOrFXGBP && formatters.commasExact4Digits(price)}
      <br />
      {isFutures ? (
        <>
          Contracts:{' '}
          <InputNumber
            value={Math.floor(quantity / lotSize)}
            step={1}
            formatter={formatters.commasNoDigits}
            onChange={val => setQuantity((Number(val) || 0) * lotSize)}
            style={{ width: 150 }}
            status={quantityInputStatus}
            styles={quantityInputStyles}
          />
          {`/ ${formatters.commasNoDigits(maxContracts)}     `}
          <div style={{ color: '#8c8c8c', marginTop: 4 }}>
            (1 contract = {formatters.commasNoDigits(lotSize)}
            {asset.lot_unit ? ` ${asset.lot_unit}` : ''})
          </div>
        </>
      ) : (
        <>
          Quantity:{' '}
          <InputNumber
            value={quantity}
            formatter={formatters.commasNoDigits}
            onChange={val => setQuantity(Number(val || 0))}
            style={{ width: 150 }}
            status={quantityInputStatus}
            styles={quantityInputStyles}
          />
          {`/ ${formatters.commasNoDigits(maxQuantity)}     `}
        </>
      )}
      {quantityTooLarge && (
        <div role="alert" style={{ color: '#d54848', marginTop: 4 }}>
          Maximum allowed is{' '}
          {formatters.commasNoDigits(isFutures ? maxContracts : maxQuantity)}.
        </div>
      )}
      {/*
         Info Popovers
      */}
      {isBondAndSell && (
        <div>
          (Owned qty.: {formatters.commasNoDigits(instrument.amount)}; Repoed
          qty.: {formatters.commasNoDigits(instrument.repoed_amount)})
        </div>
      )}
      {showOverLimitIcon && <OverLimitIcon financialInstrument={instrument} />}
      {showCannotSellIcon && <CannotSellIcon allowShort={allow_short_sell} />}
      {showCannotShortIcon && (
        <CannotShortSellIcon shortSellLimit={short_sell_leverage} />
      )}
      {showCannotShortIcon2 && (
        <CannotShortSellIcon2 pv_limit={instrument.pv_limit} />
      )}
      {cantCoverCollateral && <CannotCoverMargin />}
      {cannotBuy && <CannotBuyIcon symbol={currency.symbol} />}
      {/*
         Info Popovers
      */}
      {!isBondAndSell && !isFutures && <br />}
      {v2CapacityApplied ? (
        <>
          Notional limit:{' '}
          <span style={{ color: 'black' }}>
            {notionalCapacity.result?.configured_limit_reporting == null
              ? '—'
              : formatters.commas2Digits(
                  Number(notionalCapacity.result.configured_limit_reporting),
                )}
          </span>{' '}
          {reportingCurrency.symbol}
          {capacityStatus ? (
            <div style={{ color: '#8c8c8c', marginTop: 4 }}>
              {capacityStatus}
            </div>
          ) : null}
        </>
      ) : (
        <>
          Limit:{' '}
          <span style={{ color: 'black' }}>
            {formatters.percent0Digits(instrument.pv_limit)}
          </span>{' '}
          AUM {reportingCurrency.symbol}
        </>
      )}
      <br />
      Order Type:{' '}
      <Radio.Group
        onChange={e => setOrderType(e.target.value)}
        value={orderType}
      >
        <MarketNotOpenPopover
          visible={!isOpen}
          geography={`${asset.geography}`}
          assetMarketHours={assetMarketHours}
          asset={asset}
        >
          <Radio value="MarketOrder" disabled={!isOpen}>
            Market
          </Radio>
        </MarketNotOpenPopover>
        {scenario_type !== HISTORICAL_SCENARIO && (
          <Radio value="LimitOrder">Limit</Radio>
        )}

        {showMooMocRadioButtons ? (
          <>
            <Radio value="MarketOnOpenOrder">MOO</Radio>
            <Radio value="MarketOnCloseOrder">MOC</Radio>
          </>
        ) : null}
      </Radio.Group>
      <br />
      Limit Price:{' '}
      <InputNumber
        style={{ marginRight: 5 }}
        disabled={!(orderType === 'LimitOrder')}
        value={limitPrice}
        min={0}
        onChange={val => setLimitPrice(Number(val || 0))}
      />
      {isBond && <div style={{ display: 'inline-block' }}>%</div>}
      <br />
      What is your target price?
      <Slider
        marks={{ '-50': '-50%', 50: '50%' }}
        min={-50}
        max={50}
        value={slideValue}
        onChange={val => {
          setSlideValue(val as number);
          setForecastValue(Math.floor(price * (1 + (val as number) / 100)));
          setEditedForecast(true);
        }}
      />
      <InputNumber
        value={forecastValue}
        onChange={val => {
          const nextValue = Number(val || 0);
          setForecastValue(nextValue);
          setEditedForecast(true);
          setSlideValue((nextValue / price - 1) * 100);
        }}
      />
      {isBondAndRealTime && (
        <div>
          <br />
          <Card>
            <Row justify="center">
              <h3>Bond Pricing</h3>
            </Row>
            <Row justify="space-between" align="middle">
              <Col>
                Clean Price
                <br />
                {displayCleanPrice}
              </Col>
              <Col>
                Dirty Price
                <br />
                {formatters.commas2Digits(price * 100)} %
              </Col>
              <Col>
                Accrued Interest
                <br />
                {displayAccruedInterest}
              </Col>
              <Col>
                Dirty Amount
                <br />
                {formatters.commasNoDigits(price * quantity)} {currency.symbol}
              </Col>
            </Row>
          </Card>
          <br />
        </div>
      )}
      <Card>
        <Row justify="center">
          <h3>Order Summary</h3>
        </Row>
        <Row justify="space-between" align="middle">
          <Col>
            {isFutures ? 'Contracts' : 'Quantity'}
            <br />
            {isFutures
              ? formatters.commasNoDigits(
                  formatters.unitsToContracts(quantity, lotSize),
                )
              : formatters.commasNoDigits(quantity)}
          </Col>
          {/*{!isBond && (*/}
          <Col>
            Price
            <br />
            {isBond && <div>{formatters.commas2Digits(price * 100)} %</div>}
            {isFX && formatters.commas4Digits(price)}
            {isNotBondOrFXnonGBP && formatters.commas2Digits(price)}
            {isNotBondOrFXGBP && formatters.commasExact4Digits(price)}
          </Col>
          {/*)}*/}
          <Col>
            Value
            <br />
            {formatters.commasNoDigits(value)}
          </Col>
          <Col>
            Fees*
            <br />
            {formatters.percent1Digit(transaction_fee)}
          </Col>
          <Col>
            Total
            <br />
            {formatters.commasNoDigits(total)}
          </Col>
        </Row>
        <br />
        {risk_approach === CASH_APPROACH && (
          <Row>Available Cash: {formatters.commasNoDigits(wallet.amount)}</Row>
        )}
        {
          isLeveraged && displayCollateral
          // <Row>Collateral: {formatters.commasNoDigits(collateral)}</Row>
        }
        {leveragedBondCollateralClause &&
          'Collateral will be reserved only at order fill'}
      </Card>
      <TextArea
        maxLength={511}
        minLength={10}
        placeholder={investmentRationalePlaceholder}
        value={investmentRationale}
        onChange={e => setInvestmentRationale(e.target.value)}
      />
      <Radio.Group value={term} onChange={e => setTerm(e.target.value)}>
        {Object.entries(TERMS).map(([key, val]) => (
          <Radio value={key} key={key} style={{ width: 100 }}>
            {val}
          </Radio>
        ))}
      </Radio.Group>
      <br />
      <Radio.Group value={analysis} onChange={e => setAnalysis(e.target.value)}>
        {Object.entries(ANALYSIS_TYPES).map(([key, val]) => (
          <Radio value={key} key={key} style={{ width: 100 }}>
            {val}
          </Radio>
        ))}
      </Radio.Group>
    </Modal>
  );
};

export default OrderModal;
